import { Can, Cannot } from './components/Can';
import { FC, useEffect, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { AppPermissions } from './constants/AppPermissions';
import { DefaultRoutes } from 'Routes';
import Forbidden from './pages/Forbidden/Forbidden';
import { Loader } from '@trackman/web-shared-components';
import { Notifications } from 'components/Notifications';
import TagManager from 'react-gtm-module';
import ThemeProvider from '@trackman/web-shared-components/styles/Theme';
import { i18nInit } from 'i18nSetup';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';
import { PageLayout } from 'components/PageLayout/PageLayout';
import { useCanUserSeeOrganizations, useIsUserAdmin } from 'queryHooks/permissions';

interface IProps {
  children?: React.ReactNode;
}

export const App: FC<IProps> = observer((props) => {
  const stores = useStores();
  const as = stores.authenticationService;
  const location = useLocation();

  const { data: userOrganizationsAsInstructor, isPending: isUserOrganizationsPending } = useCanUserSeeOrganizations(as.isUserLoggedIn);
  const { data: isUserAdmin, isPending: isUserAdminPending } = useIsUserAdmin(as.isUserLoggedIn);

  i18nInit();

  useEffect(() => {
    const tagManagerArgs = {
      gtmId: process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID,
    };

    if (tagManagerArgs.gtmId) {
      try {
        TagManager.initialize(tagManagerArgs);
        console.log('Google Tag Manager initialized successfully');
      } catch (error) {
        console.error('Failed to initialize Google Tag Manager:', error);
      }
    } else if (!tagManagerArgs.gtmId) {
      console.warn('Google Tag Manager ID is not defined in environment variables');
    }
  }, []);

  useEffect(() => {
    as.checkIfUserIsLoggedIn();
  }, [as]);

  useEffect(() => {
    localStorage.removeItem('calculatorPreviewCulture');
  }, [location.pathname]);

  const isLandingPage = useMemo(() => location.pathname === DefaultRoutes.LandingPage, [location.pathname]);
  const noAccessToOrgPage = location.pathname.startsWith(DefaultRoutes.Organization) && userOrganizationsAsInstructor?.length === 0;
  const noAccessToAdminPage = location.pathname.includes(DefaultRoutes.Admin) && !isUserAdmin;

  return (
    <ThemeProvider>
      {as.isLoadingUserLoggedIn || (as.isUserLoggedIn && isUserAdminPending && isUserOrganizationsPending) ? (
        <div className='loader-wrapper'>
          <Loader size='large' />
        </div>
      ) : (
        <>
          {as.isUserLoggedIn && isLandingPage && <Navigate to={DefaultRoutes.Home} replace={true} />}
          {as.isUserLoggedIn && noAccessToOrgPage && <Navigate to={DefaultRoutes.NoAccess} replace={true} />}
          {as.isUserLoggedIn && noAccessToAdminPage && <Navigate to={DefaultRoutes.NoAccess} replace={true} />}
          {as.isUserLoggedIn && !isLandingPage && (
            <>
              <Notifications />
              <Can need={AppPermissions.PortalAccess}>
                <PageLayout>{props.children}</PageLayout>
              </Can>
              <Cannot need={AppPermissions.PortalAccess}>
                <Forbidden />
              </Cannot>
            </>
          )}
          {!as.isUserLoggedIn && isLandingPage && <PageLayout>{props.children}</PageLayout>}
          {!as.isUserLoggedIn && !isLandingPage && <Navigate to={DefaultRoutes.LandingPage} replace={true} />}
        </>
      )}
    </ThemeProvider>
  );
});

export default App;
